import React from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente: ExpedienteDespacho
  mostrarSelloVerde?: boolean
}

export const HojaDODASAT: React.FC<Props> = ({ expediente, mostrarSelloVerde = true }) => {
  const anio = '26'
  const aduana = '160' // Manzanillo
  const patente = expediente.actores.agenteAduanal.patente || '3944'
  const consecutivo = expediente.folio.replace(/[^0-9]/g, '').slice(-7).padStart(7, '4')
  const numPedimento = `${anio}  ${aduana.slice(0, 2)}  ${patente}  ${consecutivo}`
  const pedimentoCompacto = `${anio}${aduana.slice(0, 2)}${patente}${consecutivo}`

  const esExportacion = expediente.tipoOperacion === 'EXPORTACION'
  const clavePedimento = expediente.regimen === 'IR_RFE' ? 'IR' : expediente.regimen === 'IN_IMMEX' ? 'IN' : 'A1'
  const folioDODA = `DODA-${anio}-${aduana}-${consecutivo}`

  const caat = expediente.actores.transportista.caat || '8U22'
  const placas = expediente.actores.transportista.placas || '44-AK-9P'
  const chofer = expediente.actores.transportista.chofer || 'Roberto Valdés Martínez'
  const transportista = expediente.actores.transportista.razonSocial || 'Autotransportes del Pacífico S.A.'

  const contenedorNum = expediente.contenedor.numero
  const sellos = expediente.contenedor.sellos || 'MX-SAT-992144'
  const valorUSD = expediente.documentos.factura.valorUSD || 45000
  const pesoKg = expediente.contenedor.pesoBrutoKg || 22000

  // Cadena digital del DODA para el QR de Garita Fiscal
  const qrCadenaDODA = `DODA|ADUANA:${aduana}|SECCION:0|PATENTE:${patente}|PEDIMENTO:${pedimentoCompacto}|TIPO:${esExportacion ? '2' : '1'}|CLAVE:${clavePedimento}|CAAT:${caat}|PLACAS:${placas}|CONTENEDOR:${contenedorNum}|SELLOS:${sellos}|VGM:${pesoKg}|FECHA:${new Date().toISOString()}`
  const selloDigitalSAT = `FEA|SAT|ANAM|${folioDODA}|${pedimentoCompacto}|e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855`

  return (
    <div className="relative w-full bg-[#fdfdfc] text-slate-900 shadow-2xl rounded-sm p-6 sm:p-10 font-sans border border-slate-300 print:border-none print:shadow-none print:p-4 text-[10px] sm:text-[11px] leading-tight select-none">
      {/* Marca de Agua Oficial */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.035] overflow-hidden">
        <div className="text-[100px] font-black uppercase text-slate-900 -rotate-45 select-none tracking-widest whitespace-nowrap">
          ANAM DODA PITA
        </div>
      </div>

      {/* Encabezado Oficial DODA */}
      <div className="border-b-2 border-slate-900 pb-2 mb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-0.5">
            <p className="text-[8px] font-black uppercase tracking-widest text-slate-600">
              SECRETARÍA DE HACIENDA Y CRÉDITO PÚBLICO
            </p>
            <p className="text-[9px] font-black uppercase tracking-wider text-slate-800">
              AGENCIA NACIONAL DE ADUANAS DE MÉXICO (ANAM)
            </p>
            <h1 className="text-base sm:text-lg font-black tracking-tight text-slate-950 font-mono">
              DOCUMENTO DE OPERACIÓN PARA DESPACHO ADUANERO (DODA)
            </h1>
            <p className="text-[8px] font-mono text-slate-500">
              CUMPLIMIENTO DE LAS REGLAS 3.1.33 Y 3.1.34 DE LAS REGLAS GENERALES DE COMERCIO EXTERIOR
            </p>
          </div>

          <div className="text-right flex flex-col items-end">
            <span className="text-[9px] font-bold uppercase bg-slate-900 text-white px-2.5 py-0.5 rounded-sm font-mono">
              MODULACIÓN ELECTRÓNICA
            </span>
            <span className="font-mono text-[9px] text-slate-600 mt-1 font-bold">
              FOLIO: {folioDODA}
            </span>
            <span className="font-mono text-[8px] text-slate-500">
              EMISIÓN: {new Date().toLocaleDateString('es-MX')} {new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* BLOQUE CENTRAL: CÓDIGO QR BIDIMENSIONAL Y CÓDIGO DE BARRAS DE GARITA */}
      {/* ========================================================================= */}
      <div className="p-4 mb-3 rounded-lg border-2 border-slate-900 bg-slate-50/80 flex flex-col sm:flex-row items-center justify-around gap-4">
        {/* Código QR DODA Escaneable */}
        <div className="flex flex-col items-center gap-1.5 p-2 bg-white rounded-md border border-slate-300 shadow-sm">
          <QRCodeSVG
            value={qrCadenaDODA}
            size={130}
            level="M"
            includeMargin={true}
          />
          <span className="font-mono text-[8px] font-bold text-slate-600">
            ESCANEAR EN GARITA FISCAL
          </span>
        </div>

        {/* Datos Clave de Activación */}
        <div className="space-y-1.5 font-mono text-left w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-sans font-bold text-slate-500 uppercase">Aduana / Sección:</span>
            <span className="font-bold text-xs text-slate-950">160 - MANZANILLO, COLIMA (SECCIÓN I TEC I SSA)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-sans font-bold text-slate-500 uppercase">Patente Aduanal:</span>
            <span className="font-bold text-xs text-slate-950">{patente}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-sans font-bold text-slate-500 uppercase">Pedimento Declarado:</span>
            <span className="font-black text-xs text-indigo-900 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">
              {numPedimento}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-sans font-bold text-slate-500 uppercase">Operación & Régimen:</span>
            <span className="font-bold text-xs text-slate-950">
              {esExportacion ? '2 - EXPORTACIÓN' : '1 - IMPORTACIÓN'} · CLAVE {clavePedimento}
            </span>
          </div>

          {/* Simulación de Código de Barras Horizontal */}
          <div className="pt-2 flex flex-col items-center">
            <div className="h-6 w-52 flex items-center justify-between px-1 bg-white border border-slate-400">
              {Array.from({ length: 48 }).map((_, i) => (
                <div
                  key={i}
                  className={`h-full ${i % 3 === 0 ? 'w-1 bg-black' : i % 5 === 0 ? 'w-1.5 bg-black' : 'w-0.5 bg-black'}`}
                />
              ))}
            </div>
            <span className="font-mono text-[7px] text-slate-600 mt-0.5 tracking-widest">
              *{folioDODA.replace(/-/g, '')}*
            </span>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* CUADRÍCULA 1: TRANSPORTE Y CONTENEDOR */}
      {/* ========================================================================= */}
      <div className="border-2 border-slate-900 mb-3 divide-y divide-slate-900">
        <div className="bg-slate-200/80 px-2 py-1 font-bold text-[9px] uppercase tracking-wider text-slate-800">
          1. DATOS DEL TRANSPORTE, TRACTOCAMIÓN Y CONTENEDOR
        </div>
        <div className="grid grid-cols-12 divide-x divide-slate-900 font-mono text-[9px]">
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">CAAT TRANSPORTISTA</span>
            <span className="font-bold text-slate-950">{caat}</span>
          </div>
          <div className="col-span-6 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">EMPRESA TRANSPORTISTA / OPERADOR</span>
            <span className="font-bold text-slate-950 truncate block">{transportista} ({chofer})</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">PLACAS TRACTO</span>
            <span className="font-bold text-slate-950">{placas}</span>
          </div>
        </div>

        <div className="grid grid-cols-12 divide-x divide-slate-900 font-mono text-[9px]">
          <div className="col-span-4 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">CONTENEDOR / EQUIPO</span>
            <span className="font-bold text-slate-950">{contenedorNum} ({expediente.contenedor.tipo})</span>
          </div>
          <div className="col-span-4 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">CANDADOS / SELLOS FISCALES</span>
            <span className="font-bold text-slate-950">{sellos}</span>
          </div>
          <div className="col-span-4 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">PESO BRUTO (VGM KG)</span>
            <span className="font-bold text-slate-950">{pesoKg.toLocaleString()} KG</span>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* CUADRÍCULA 2: ACTORES Y PARTIDAS */}
      {/* ========================================================================= */}
      <div className="border-2 border-slate-900 mb-3 divide-y divide-slate-900">
        <div className="bg-slate-200/80 px-2 py-1 font-bold text-[9px] uppercase tracking-wider text-slate-800">
          2. DATOS DE IMPORTADOR / EXPORTADOR Y CONSIGNATARIO
        </div>
        <div className="grid grid-cols-12 divide-x divide-slate-900 font-mono text-[9px]">
          <div className="col-span-6 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">EMISOR / SHIPPER (EXPORTADOR)</span>
            <span className="font-bold text-slate-950 block truncate">{expediente.actores.emisor.nombre}</span>
            <span className="text-[8px] text-slate-600">RFC: {expediente.actores.emisor.rfc}</span>
          </div>
          <div className="col-span-6 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">RECEPTOR / CONSIGNATARIO (IMPORTADOR)</span>
            <span className="font-bold text-slate-950 block truncate">{expediente.actores.receptor.nombre}</span>
            <span className="text-[8px] text-slate-600">TAX ID / RFC: {expediente.actores.receptor.rfc}</span>
          </div>
        </div>

        <div className="grid grid-cols-12 divide-x divide-slate-900 font-mono text-[9px]">
          <div className="col-span-6 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">MERCANCÍA PRINCIPAL</span>
            <span className="font-bold text-slate-950 block truncate">{expediente.titulo}</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">VALOR COMERCIAL USD</span>
            <span className="font-bold text-slate-950">${valorUSD.toLocaleString()} USD</span>
          </div>
          <div className="col-span-3 p-1.5">
            <span className="block text-[8px] font-sans font-bold text-slate-500">INCOTERM DECLARADO</span>
            <span className="font-bold text-slate-950">{expediente.documentos.factura.incoterm || 'FOB Manzanillo'}</span>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* CUADRÍCULA 3: CERTIFICACIÓN DIGITAL DEL SAT Y SELLO DE MODULACIÓN */}
      {/* ========================================================================= */}
      <div className="border-2 border-slate-900 mb-3 p-2 bg-slate-50/50 font-mono text-[8px] space-y-1">
        <p className="font-bold text-slate-800 text-[9px]">SELLO DIGITAL DEL SERVICIO DE ADMINISTRACIÓN TRIBUTARIA Y ANAM:</p>
        <p className="break-all text-slate-600 leading-tight">{selloDigitalSAT}</p>
        <p className="text-slate-500 pt-1 border-t border-slate-300">
          La presentación del DODA ampara el despacho aduanero y sustituye la impresión del pedimento en papel conforme al Art. 43 de la Ley Aduanera.
        </p>
      </div>

      {/* Sello Verde / Rojo de Modulación si aplica */}
      {mostrarSelloVerde && (
        <div className="mt-2 flex items-center justify-between p-2 rounded-lg border border-emerald-500/30 bg-emerald-50 text-emerald-900 font-mono text-[9px]">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-bold">MECANISMO DE SELECCIÓN AUTOMATIZADO: DESADUANAMIENTO LIBRE (SEMANA SAT 2026)</span>
          </div>
          <span className="font-black tracking-wider text-emerald-800">CÓDIGO: VERDE_01_OK</span>
        </div>
      )}
    </div>
  )
}
